import { api } from './client';
import type { Rol } from '../auth/types';
import type { IvaEsperado } from '../lib/ivaFactura';

// ---------- Solicitudes ----------

export type EstadoSolicitud =
  | 'BORRADOR'
  /** Enviada; la app creó la OC y Administración tiene que armar la pre pago. */
  | 'PENDIENTE_PRE_PAGO'
  /** Orden de pre pago cargada; espera la firma del autorizador. */
  | 'PENDIENTE_APROBACION'
  /**
   * Falta la factura del proveedor, y la sube solo el comercial. Es el freno de
   * antes de Administración: sin factura no se arma la orden de pre pago (la OC
   * ya existe en el ERP). Después de aprobar significa lo mismo para el pago,
   * pero ese camino ya no se alcanza.
   */
  | 'PENDIENTE_FACTURA'
  /** Con la factura cargada; le toca pagar a Administración. */
  | 'PENDIENTE_PAGO'
  | 'PAGADA'
  /** La mercadería llegó. Es el estado final. */
  | 'ARRIBADO'
  | 'ARRIBADO_CON_DIFERENCIAS'
  | 'OBSERVADA'
  | 'CANCELADA';

export type PendienteDe =
  | 'SOLICITANTE'
  | 'ADMINISTRACION'
  | 'AUTORIZADOR'
  | 'RECEPCION'
  | 'SISTEMA'
  | 'NINGUNO';

export type OrigenArticulo = 'EXISTENTE' | 'REACTIVADO';

export type TipoCompra = 'STOCK' | 'INTERNA';

/** Condición de IVA del ERP (`codigotipo` + el % que se le aplica). */
export type CondicionIva = {
  codigo: string;
  descripcion: string;
  alicuota: number;
};

/** Lo que devuelve el alta: el proveedor y qué pasó en el ERP. */
export type ProveedorCreado = Proveedor & {
  erp: { creado: true; codigo: string } | { creado: false; error: string };
};

export type Proveedor = {
  id: string;
  flexxusId: string | null;
  razonSocial: string;
  cuit: string | null;
  origen: string;
  activo: boolean;
  /**
   * Alícuota general del proveedor en % (`tipoivacompra.iva1` del ERP): 21 si es
   * Responsable Inscripto, 27 si es RI de servicios, 0 si es monotributista o
   * exento. Es la base del IVA de todos los renglones de la compra.
   */
  alicuotaIva: number | string;
};

export type Renglon = {
  id: string;
  articuloCodigo: string | null;
  descripcion: string;
  observacion: string | null;
  unidad: string | null;
  cantidad: number | string;
  /** Lo que tipeó el solicitante: final o neto según `preciosConIva`. */
  precioIngresado: number | string;
  /** Alícuota efectiva del renglón en % (21, 10.5, 0…). */
  alicuotaIva: number | string;
  /** Coeficiente del rubro con el que se derivó la alícuota (1 o 0.5). */
  coeficienteIva: number | string;
  /** Neto derivado — es lo que viaja a la OC de Flexxus. */
  precioNeto: number | string;
  subtotal: number | string;
  origenArticulo: OrigenArticulo;
  cantidadRecibida: number | string;
  orden: number;
};

export type Solicitud = {
  id: string;
  numero: string;
  solicitanteId: string;
  tipoCompra: TipoCompra;
  proveedorId: string | null;
  codigoFormaPagoFlexxus: string | null;
  codigoMonedaFlexxus: string | null;
  /** Usuario del ERP a nombre de quien queda la orden (lo elige quien la carga). */
  codigoUsuarioAsignadoFlexxus: string | null;
  cotizacion: number | string | null;
  estado: EstadoSolicitud;
  estadoDesde: string;
  pendienteDe: PendienteDe;
  /** Motivo de la última devolución o rechazo, lo escribe quien revisa. */
  observacionActual: string | null;
  /** Texto libre del solicitante: va al PDF de la orden y a los correos. */
  observaciones: string | null;
  /** true = los precios se cargaron con IVA incluido y la app desglosó el neto. */
  preciosConIva: boolean;
  totalNeto: number | string;
  /** Total con IVA estimado con las alícuotas de los artículos. */
  totalConIva: number | string;
  /** N° del PAGO en Flexxus que el contador cargó al registrarlo (desde el 2026-09-24). */
  comprobanteNumero: string | null;
  /**
   * N° del pago que devolvió Flexxus cuando se pagó desde la app (2026-09-25).
   * Con él la compra ya no se puede volver a pagar, devolver ni cancelar, y el
   * registro del pago tiene que llevar este número.
   */
  pagoNumeroFlexxus?: string | null;
  totalAPagar: number | string | null;
  /** N° de la orden de compra que devolvió Flexxus al crearla la app. */
  ocNumeroFlexxus: string | null;
  /** Cuándo quedó creada esa orden en el ERP. */
  ocGeneradaEn: string | null;
  /** N° de la orden de pre pago que armó el contador en Flexxus y cargó acá. */
  prePagoNumeroFlexxus: string | null;
  /**
   * Compras que van en UNA misma orden de pre pago comparten este id (2026-09-24):
   * se aprueban y se pagan juntas. `null` = la compra va sola.
   */
  grupoPagoId?: string | null;
  /** Las OTRAS compras del grupo. Solo viene en el detalle (`obtenerSolicitud`). */
  grupoPago?: CompraDelGrupo[];
  creadoEn: string;
  actualizadoEn: string;
  renglones: Renglon[];
  proveedor?: Proveedor | null;
  solicitante?: {
    id: string;
    nombre: string;
    email: string;
    /** Fallback del usuario asignado en solicitudes viejas (antes se usaba este). */
    codigoUsuarioFlexxus?: string | null;
  } | null;
  documentos?: DocumentoDto[];
};

/** Otra compra de la misma orden de pre pago, con lo justo para listarla. */
export type CompraDelGrupo = {
  id: string;
  numero: string;
  ocNumeroFlexxus: string | null;
  estado: EstadoSolicitud;
  totalNeto: number | string;
  totalConIva: number | string;
  codigoMonedaFlexxus: string | null;
  cotizacion: number | string | null;
  solicitante: { nombre: string };
  /** Sus adjuntos: al pagar el grupo, el contador necesita la factura y la OC de cada una. */
  documentos?: DocumentoDto[];
};

/**
 * Listado del ERP con el total real de coincidencias.
 *
 * Flexxus corta los listados y el total viaja aparte: una búsqueda con 1042
 * coincidencias devuelve 200 y `truncado: true`. Los buscadores lo usan para
 * avisar que hay que afinar la búsqueda, en vez de dejar creer que lo que se
 * busca no existe.
 */
export type ResultadoLista<T> = {
  items: T[];
  total: number;
  truncado: boolean;
};

/** Fallback cuando la búsqueda falla: lista vacía con el mismo shape. */
export const LISTA_VACIA: ResultadoLista<never> = { items: [], total: 0, truncado: false };

export type ArticuloBuscado = {
  /** Correlativo interno que asigna Flexxus. */
  codigo: string;
  /** Código propio / del proveedor: el que elige la persona al dar el alta. */
  codigoParticular: string | null;
  descripcion: string;
  unidad: string | null;
  /** Descripción del rubro ("SOFTWARE"). */
  rubro: string | null;
  /** Código del rubro ("SOF"). */
  codigoRubro: string | null;
  activoFlexxus: boolean;
  /**
   * Alícuota **propia** del artículo en %. Hoy está en 0 en todo el catálogo del
   * ERP: la alícuota real sale del proveedor por `coeficienteIva`.
   */
  alicuotaIva: number;
  /**
   * Coeficiente de IVA **del artículo**, ya resuelto por el ERP: 1 = alícuota
   * general del proveedor, 0.5 = la mitad (21 % → 10,5 %). No es el del rubro:
   * un artículo con `coeficientesegunrubro=false` tiene el suyo y le gana.
   */
  coeficienteIva: number;
  /** Último precio de compra registrado en Flexxus; null si no lo informa. */
  ultimoPrecioCompra: number | null;
};

export type ArticuloParaClonar = {
  codigo: string;
  descripcion: string;
  codigorubro: string;
  rubro: string;
  codigounidadmedida: string;
  unidad: string;
  codigomarca: string;
  marca: string;
  codigomoneda: string;
  codigosuperrubro: string;
  alicuotaiva: number;
  esexento: number;
  coeficiente: number;
  coeficientesegunrubro: boolean;
  preciosegunrubro: number;
  preciocompra: number;
  precioventa: number[];
  margen: number[];
  fraccionado: boolean;
  clasearticulo: string;
  biendecambio: boolean;
  parteconjunto: string;
  permitedetalle: boolean;
  codigoparticular: string;
};

/**
 * El `codigoarticulo` NO se manda: es readOnly en el ERP y siempre lo asigna
 * Flexxus. El código que elige la persona es `codigoparticular` (40 chars).
 */
export type AltaArticuloBody =
  | {
      modo: 'clonar';
      codigoOrigen: string;
      descripcion: string;
      codigoparticular?: string;
    }
  | {
      modo: 'nuevo';
      descripcion: string;
      codigorubro: string;
      codigounidadmedida: string;
      codigomarca: string;
      codigomoneda?: string;
      codigoparticular?: string;
      /**
       * Alícuota de IVA del artículo: 21 o 10,5. El ERP la guarda como
       * coeficiente (1 / 0,5) y la traduce el back. Si no se manda, 21 %.
       */
      ivaCompra?: number;
      preciocompra?: number;
    };

/**
 * Una retención que le corresponde a un proveedor, con lo que hace falta para
 * calcularla. La API **no calcula el importe**: la app lo hace con estos datos y
 * con lo ya pagado en el mes (`calcularRetencion`).
 */
export type RetencionProveedor = {
  codigoRetencion: string;
  descripcion: string;
  /** 100 = exento, no se le retiene. */
  porcentajeExencion: number;
  /** 1 = porcentaje fijo · 0 = por escalas progresivas. */
  tipoPorcentaje: number | null;
  porcentajeFijo: number | null;
  /** Mínimo no imponible: se retiene sobre el excedente. */
  sobreExcedente: number | null;
  retencionMinima: number | null;
  /** Tramos de la escala. >0 ⇒ no es un porcentaje simple. */
  escalas: number;
  /** Sobre el neto (Ganancias) o sobre el total (monotributo). */
  delNeto: boolean;
  /** >0 ⇒ la base es el IVA (`RETIVA-M`). */
  porcentajeAplicacionIva: number;
};

/**
 * Lo que ya se le pagó al proveedor en el mes, que el ERP acumula para
 * Ganancias: el mínimo no imponible se descuenta una vez por mes y se resta lo
 * ya retenido.
 */
export type AcumuladoMesProveedor = {
  mes: string;
  pagos: Array<{
    numeroPago: number;
    fecha: string;
    netoPagado: number;
    totalPagado: number;
    retenciones: Record<string, number>;
  }>;
  netoPagado: number;
  totalPagado: number;
  retenido: Record<string, number>;
  facturasRevisadas: number;
};

/** Slugs de los catálogos que sirve /api/config/catalogos/:tipo. */
export type CatalogoSlug =
  | 'rubros'
  | 'superrubros'
  | 'familias'
  | 'categorias'
  | 'marcas'
  | 'monedas'
  | 'depositos'
  | 'centros-costo'
  | 'formas-pago'
  | 'unidades-medida'
  // Medio de pago de un pago a proveedor.
  | 'bancos'
  | 'cuentas-banco'
  | 'chequeras';

/**
 * La factura del proveedor tal como la tiene el ERP: contra ella se aplica el
 * pago. **Los importes están en pesos aunque la factura sea en dólares**, y
 * `neto` es sin impuestos (la base de las retenciones de Ganancias).
 */
export type ComprobanteCompraErp = {
  tipoComprobante: string;
  numeroComprobante: number;
  fecha: string;
  neto: number;
  /** Lo no gravado: no entra en la base de las retenciones de Ganancias. */
  exento: number;
  totalBruto: number;
  pagado: number;
  /** Cortado al centavo: es lo que se cancela (la API de pagos no maneja más decimales). */
  saldo: number;
  /** El saldo con los decimales que guarda el ERP (397.310,3966). */
  saldoExacto?: number;
  codigoMoneda: string;
  cotizacion: number;
  anulada: boolean;
};

/** En qué quedó la OC en Flexxus, según lo recibido de sus renglones (2026-10-28). */
export type EstadoFacturacionOc = 'FACTURADA' | 'EN_PARTE' | 'SIN_FACTURA' | 'ANULADA';

/** Si la OC ya tiene su factura en Flexxus: el estado y las facturas que la citan (para el número). */
export type FacturacionOc = {
  /** `null` si Flexxus no tiene la OC. */
  estado: EstadoFacturacionOc | null;
  facturas: ComprobanteCompraErp[];
};

/** Un renglón de la OC que falta facturar, leído del ERP. */
export type RenglonOcPendiente = {
  linea: number;
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  bonificacion: number;
  neto: number;
};

/** Los papeles del pago que genera la app (2026-10-28). */
export type PapelesDelPago = {
  numeroPago: number;
  ordenPago: string;
  constancias: string[];
  /** Retenciones sin constancia generada (no son de Ganancias): se suben a mano. */
  sinConstancia: string[];
};

/** Lo que devuelve el back para armar la pantalla «Crear la factura»: una por OC. */
export type PreparacionFactura = {
  /** `condicionIva`: el código corto del ERP (RI, RI2, EX, MO…). */
  proveedor: {
    codigo: string;
    razonSocial: string;
    cuit: string | null;
    condicionIva: string | null;
  };
  compra: { id: string; numero: string; ocNumero: number };
  /** Lo pendiente de la OC. `alicuotaIva` sale del renglón de la compra; `null` si no se pudo emparejar. */
  renglones: Array<RenglonOcPendiente & { alicuotaIva: number | null }>;
  /** El neto de la factura: lo pendiente de la OC. */
  neto: number;
  /** Las facturas del ERP que ya citan la OC: si hay, no se crea otra. */
  facturasExistentes: ComprobanteCompraErp[];
  /** La factura del proveedor que subió el comercial. */
  documentos: DocumentoDto[];
  /** El IVA que corresponde por alícuota según los artículos, de mayor a menor. */
  ivaEsperado: IvaEsperado[];
  renglonesSinAlicuota: number;
  /** Neto + IVA esperado, sin percepciones. */
  total: number;
  /** Según la condición de IVA del proveedor: inscripto → FA, monotributo o exento → FC. */
  tipoSugerido: 'FA' | 'FC';
  codigoDeposito: string;
  codigoMultiplazo: number;
  percepciones: Array<{ codigo: string; descripcion: string; porcentajeSugerido: number }>;
};

/**
 * Lo que manda la pantalla. El neto no viaja (sale de las OC); el IVA va como
 * quedó en pantalla y el back decide si se lo pasa a Flexxus o lo deja calcular.
 */
export type DatosFacturaPapel = {
  /** La casilla «Revisé contra la factura del proveedor». */
  revisado: true;
  tipoComprobante: string;
  puntoVenta: number;
  numero: number;
  fecha: string;
  vencimiento: string;
  iva: Array<{ porcentaje: number; monto: number }>;
  percepciones: Array<{ codigo: string; monto: number; numero?: string }>;
};

export type FacturaCreada = {
  tipoComprobante: string;
  numeroComprobante: number;
  numeroAsiento: number | null;
  simulado: boolean;
  payload: Record<string, unknown>;
  /** Como quedó en el ERP; `null` si simulada o si no se pudo releer. */
  factura: ComprobanteCompraErp | null;
  /** El total que mostró la pantalla, para compararlo con el que calculó Flexxus. */
  totalEsperado: number;
};

export type CatalogoItem = {
  codigo: string;
  descripcion: string;
  activo: boolean;
  metadata?: Record<string, unknown> | null;
};

/**
 * Una fila del listado de «duplicar»: puede ser una compra de la app o una orden
 * de compra que solo existe en Flexxus (el historial de antes de la app). Las
 * órdenes que ya tienen su compra acá vienen una sola vez, como `APP`.
 */
export type ItemDuplicable = {
  origen: 'APP' | 'FLEXXUS';
  /** Clave para React; no es un id de la base. */
  clave: string;
  /** Identificador visible: "OC 2418" o el número interno si todavía no tiene orden. */
  etiqueta: string;
  fecha: string | null;
  proveedor: string | null;
  total: number | null;
  /** Solo las de la app: el listado del ERP no informa la moneda. */
  moneda: string | null;
  estado?: EstadoSolicitud;
  solicitudId?: string;
  ocNumero?: string;
  codigoProveedorFlexxus?: string;
  /** Orden anulada en Flexxus: se puede duplicar igual. */
  anulada?: boolean;
};

export type DocumentoDto = {
  id: string;
  tipo:
    | 'FACTURA_PROVEEDOR'
    /** PDF de la orden de compra, generado por la app (no lo sube nadie). */
    | 'ORDEN_COMPRA'
    | 'ORDEN_PRE_PAGO'
    /** Comprobante de la transferencia con la que se pagó. */
    | 'COMPROBANTE_PAGO'
    /** Orden de pago de Flexxus (obligatoria al registrar el pago). */
    | 'ORDEN_PAGO'
    /** Certificado de retención del pago (opcional, pueden ser varios). */
    | 'RETENCION'
    /** Otros papeles para el proveedor, sumados al registrar el pago (2026-10-28). */
    | 'ADJUNTO_PAGO'
    | 'REMITO'
    | 'OTRO';
  nombreOriginal: string;
  mime: string;
  tamanoBytes: number;
  subidoEn: string;
};

/** Qué pasó con el respaldo del pago que se le manda al proveedor. */
export type ResultadoBackupPago =
  | { enviado: true; estado: 'enviado' | 'simulado'; para: string[]; cc: string[] }
  | { enviado: false; motivo: string };

/** Una fila de la planilla de altas, ya cruzada con la base. */
export type FilaImportada = {
  /** Número de fila tal como se ve en Excel (la 1 es el encabezado). */
  fila: number;
  email: string;
  codigo: string;
  roles: Rol[];
  nota: string | null;
  accion: 'crear' | 'actualizar';
  /** Con esto cargado, la fila no se aplica. */
  error: string | null;
  /** Aviso que no impide la carga (ya usa otro código del ERP). */
  advertencia: string | null;
  yaEntro: boolean;
  aplicada: boolean;
};

export type ImportacionPlanilla = {
  dryRun: boolean;
  total: number;
  /** Filas sin correo: se saltean, no son un error. */
  salteadas: number;
  erroresFormato: Array<{ fila: number; error: string }>;
  filas: FilaImportada[];
  aplicadas: number;
  conError: number;
};

// ---------- API helpers ----------

export const endpoints = {
  listarProveedores: (q?: string) =>
    api<ResultadoLista<Proveedor>>(
      `/api/config/proveedores${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ).catch(() => LISTA_VACIA as ResultadoLista<Proveedor>),

  /**
   * `incluirCompraInterna` viene en false porque quien busca es el renglón de
   * una solicitud, y ahí los artículos de compra interna se eligen del
   * desplegable. La pantalla "Nuevo artículo" los pide en true para clonarlos.
   */
  buscarArticulos: (q: string, incluirInactivos = true, incluirCompraInterna = false) =>
    api<ResultadoLista<ArticuloBuscado>>(
      `/api/articulos/buscar?q=${encodeURIComponent(q)}&incluirInactivos=${incluirInactivos}` +
        `&incluirCompraInterna=${incluirCompraInterna}`,
    ),

  /**
   * Los artículos habilitados para una compra INTERNA. Son pocos (hoy dos, uno
   * al 21 % y otro al 10,5 %) y se eligen de un desplegable: son los únicos que
   * hacen que el ERP impute la orden con la clase de comprobante SERVICIOS.
   */
  listarArticulosCompraInterna: () =>
    api<ArticuloBuscado[]>('/api/articulos/compra-interna'),

  /**
   * Usuarios activos del ERP (código + nombre) para el desplegable "Usuario de
   * Flexxus" de la solicitud. Si Flexxus no responde, el backend devuelve [].
   */
  listarUsuariosErp: () =>
    api<Array<{ codigo: string; nombre: string }>>('/api/usuarios-flexxus').catch(
      () => [] as Array<{ codigo: string; nombre: string }>,
    ),

  /** Trae un artículo existente aplanado, para previsualizar/clonar. */
  obtenerArticuloParaClonar: (codigo: string) =>
    api<ArticuloParaClonar>(`/api/articulos/${encodeURIComponent(codigo)}/para-clonar`),

  /** Crea un artículo en Flexxus (modo clonar o nuevo). Devuelve el código asignado. */
  crearArticulo: (body: AltaArticuloBody) =>
    api<{ ok: boolean; codigo: string; codigoParticular: string | null }>('/api/articulos', {
      method: 'POST',
      body,
    }),

  /** Código propio libre que sugiere el ERP, para el botón "Sugerir" del alta. */
  sugerirCodigoParticular: () =>
    api<{ codigoParticular: string | null }>('/api/articulos/proximo-codigo-particular'),

  /**
   * Lista un catálogo de referencia de Flexxus desde el cache local
   * (rubros, marcas, familias, categorias, superrubros, monedas, depositos,
   * centros-costo). Alimenta los desplegables de los formularios.
   */
  listarCatalogo: (tipo: CatalogoSlug, incluirInactivos = false) =>
    api<CatalogoItem[]>(
      `/api/config/catalogos/${tipo}?incluirInactivos=${incluirInactivos}`,
    ).catch(() => [] as CatalogoItem[]),

  /**
   * Alícuotas de IVA que usa la instancia (salen del maestro de tipos de IVA de
   * compras del ERP). Hoy la usa el alta de artículos; los catálogos de
   * percepciones, retenciones y tipos de comprobante se eliminaron el 2026-07-31
   * con el formulario de facturación.
   */
  listarAlicuotasIva: () =>
    api<number[]>('/api/config/alicuotas-iva').catch(() => [] as number[]),

  /**
   * Condiciones de IVA del ERP, para el alta de proveedores. A diferencia de
   * los demás catálogos, esta SÍ deja que la promesa rechace: es la única que
   * tiene un aviso de error en pantalla (`ModalProveedorNuevo.tsx`), y con el
   * `.catch(() => [])` de antes ese aviso no se podía disparar nunca — el
   * desplegable quedaba vacío sin explicación pasara lo que pasara.
   */
  listarCondicionesIva: () => api<CondicionIva[]>('/api/config/condiciones-iva'),

  listarSolicitudes: (params: Record<string, string | number | undefined> = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) qs.set(k, String(v));
    return api<{ total: number; items: Solicitud[] }>(`/api/solicitudes?${qs.toString()}`);
  },
  obtenerSolicitud: (id: string) => api<Solicitud>(`/api/solicitudes/${id}`),
  crearBorrador: (body: unknown) =>
    api<Solicitud>('/api/solicitudes', { method: 'POST', body }),
  actualizarSolicitud: (id: string, body: unknown) =>
    api<Solicitud>(`/api/solicitudes/${id}`, { method: 'PUT', body }),
  eliminarBorrador: (id: string) => api<void>(`/api/solicitudes/${id}`, { method: 'DELETE' }),
  enviarSolicitud: (id: string) =>
    api<{ estado: EstadoSolicitud; numero: string; ocNumero: string | null }>(
      `/api/solicitudes/${id}/enviar`,
      { method: 'POST' },
    ),
  duplicarSolicitud: (id: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/duplicar`, { method: 'POST' }),

  /**
   * Qué se puede duplicar entre dos fechas: las compras de la app y las órdenes
   * de compra que ya están en Flexxus, en una sola lista. Las del ERP son el
   * historial de siempre — al arrancar la app son las únicas que hay.
   */
  duplicables: (p: { desde: string; hasta: string; q?: string }) => {
    const qs = new URLSearchParams({ desde: p.desde, hasta: p.hasta });
    if (p.q) qs.set('q', p.q);
    return api<{
      items: ItemDuplicable[];
      /** El ERP no respondió: la lista trae solo lo de la app. */
      erpError: string | null;
      /** El ERP cortó el listado: hay más órdenes en el rango de las que se ven. */
      erpTruncado: boolean;
    }>(`/api/solicitudes/duplicables?${qs.toString()}`);
  },

  /**
   * Registra el pago en Flexxus y devuelve el número que asignó el ERP. No
   * cambia el estado de la compra: eso lo hace `registrarPago`.
   *
   * Con `FLEXXUS_PAGOS_ENABLED=false` (el default) devuelve `simulado: true` y
   * el payload, sin tocar el ERP.
   */
  /**
   * Un solo pago en Flexxus para la compra, o para todas las de su orden de pre
   * pago compartida: una factura por compra (2026-09-25). `monto` es la suma de
   * los saldos; el back la vuelve a verificar contra el ERP.
   */
  pagarEnFlexxus: (
    id: string,
    body: {
      facturas: Array<{ solicitudId: string; tipoComprobante: string; numeroComprobante: number }>;
      monto: number;
      observaciones?: string;
      detallepagos?: Record<string, unknown>;
    },
  ) =>
    api<{
      numeroPago: string;
      simulado: boolean;
      payload: Record<string, unknown>;
      /** Cuántas compras canceló. */
      compras: number;
      /** La orden de pago y las constancias que generó la app (2026-10-28). */
      documentos?: PapelesDelPago | null;
      /** Si no se pudieron generar: el pago igual quedó hecho. */
      errorDocumentos?: string | null;
    }>(`/api/solicitudes/${id}/pagar-flexxus`, { method: 'POST', body }),

  /** Vuelve a generar la orden de pago y las constancias de un pago hecho desde la app. */
  generarPapelesDelPago: (id: string) =>
    api<PapelesDelPago>(`/api/solicitudes/${id}/pago-flexxus/documentos`, { method: 'POST' }),

  /**
   * Las retenciones del pago hecho desde la app, con el número que les asignó
   * Flexxus (el del certificado). Se leen del ERP, no se guardan en la app.
   */
  pagoEnFlexxus: (id: string) =>
    api<{
      numeroPago: number;
      retenciones: Array<{ codigo: string; numero: number; monto: number }>;
    }>(`/api/solicitudes/${id}/pago-flexxus`),

  /**
   * Retenciones que le corresponden a un proveedor, según el ERP. Dependen de su
   * condición fiscal: no son un medio de pago ni se eligen de una lista global.
   */
  retencionesDeProveedor: (codigoFlexxus: string) =>
    api<RetencionProveedor[]>(
      `/api/config/proveedores/${encodeURIComponent(codigoFlexxus)}/retenciones`,
    ),

  /** Lo ya pagado al proveedor en el mes: la base acumulada de Ganancias. */
  pagosDelMesProveedor: (codigoFlexxus: string) =>
    api<AcumuladoMesProveedor>(
      `/api/config/proveedores/${encodeURIComponent(codigoFlexxus)}/pagos-del-mes`,
    ),

  /** La factura del proveedor en el ERP. 404 si Flexxus no la tiene. */
  comprobanteCompraErp: (codigoFlexxus: string, tipo: string, numero: number) =>
    api<ComprobanteCompraErp>(
      `/api/config/proveedores/${encodeURIComponent(codigoFlexxus)}/comprobantes/${encodeURIComponent(tipo)}/${numero}`,
    ),

  /** Las facturas del proveedor que citan la OC en Flexxus. Primero las que tienen saldo. */
  facturasDeOrdenCompra: (codigoFlexxus: string, numeroOc: string) =>
    api<ComprobanteCompraErp[]>(
      `/api/config/proveedores/${encodeURIComponent(codigoFlexxus)}/facturas-de-oc/${encodeURIComponent(numeroOc)}`,
    ),

  /** Si la OC ya tiene su factura en Flexxus (por lo recibido de sus renglones) y las facturas que la citan. */
  facturacionDeOrdenCompra: (codigoFlexxus: string, numeroOc: string) =>
    api<FacturacionOc>(
      `/api/config/proveedores/${encodeURIComponent(codigoFlexxus)}/facturacion-de-oc/${encodeURIComponent(numeroOc)}`,
    ),

  /**
   * Lo que hace falta para crear la factura de la compra (una por OC): lo
   * pendiente de la OC en el ERP, las facturas que ya la citan, la factura del
   * proveedor para compararla y las percepciones.
   */
  prepararFacturaFlexxus: (id: string) =>
    api<PreparacionFactura>(`/api/solicitudes/${id}/factura-flexxus`),

  /**
   * Crea la factura en Flexxus con los datos del papel. Con
   * `FLEXXUS_FACTURAS_ENABLED=false` (el default) vuelve `simulado: true` y el
   * pedido, sin tocar el ERP.
   */
  crearFacturaFlexxus: (id: string, body: DatosFacturaPapel) =>
    api<FacturaCreada>(`/api/solicitudes/${id}/factura-flexxus`, { method: 'POST', body }),

  /** Crea un borrador copiando una orden de compra vieja de Flexxus. */
  duplicarOrdenFlexxus: (numeroOc: string, codigoProveedor: string) =>
    api<Solicitud>('/api/solicitudes/duplicar-oc', {
      method: 'POST',
      body: { numeroOc, codigoProveedor },
    }),

  subirDocumento: async (
    solicitudId: string,
    file: File,
    tipo: DocumentoDto['tipo'],
  ): Promise<DocumentoDto> => {
    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('tipo', tipo);
    const res = await fetch(`/api/solicitudes/${solicitudId}/documentos`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error ?? `HTTP ${res.status}`);
    }
    return (await res.json()) as DocumentoDto;
  },

  eventosDe: (id: string) =>
    api<EventoDto[]>(`/api/solicitudes/${id}/eventos`),
  aprobacionesDe: (id: string) =>
    api<AprobacionDto[]>(`/api/solicitudes/${id}/aprobaciones`),
  agregarComentario: (id: string, texto: string) =>
    api(`/api/solicitudes/${id}/comentarios`, { method: 'POST', body: { texto } }),

  // Acciones de la máquina de estados

  /**
   * Administración registra la ORDEN DE PRE PAGO que armó en Flexxus. Es el paso
   * ANTERIOR a la aprobación. El PDF se sube antes con
   * `subirDocumento(id, file, 'ORDEN_PRE_PAGO')`.
   */
  registrarPrePago: (id: string, prePagoNumero: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/pre-pago`, {
      method: 'POST',
      body: { prePagoNumero },
    }),

  /**
   * UNA orden de pre pago para varias compras del mismo proveedor y la misma
   * moneda (2026-09-24). El PDF se sube antes a la PRIMERA de `solicitudIds`;
   * el back lo copia a las demás. Desde ahí se aprueban y se pagan juntas.
   */
  registrarPrePagoAgrupado: (solicitudIds: string[], prePagoNumero: string) =>
    api<{ compras: Solicitud[] }>('/api/solicitudes/pre-pago-agrupado', {
      method: 'POST',
      body: { solicitudIds, prePagoNumero },
    }),

  /**
   * El comercial cargó la factura del proveedor. El PDF se sube antes con
   * `subirDocumento(id, file, 'FACTURA_PROVEEDOR')`; esto destraba la compra si
   * estaba esperándola: pasa a Administración para la orden de pre pago, o
   * directo a "Pendiente de pago" si esa orden ya estaba armada.
   */
  cargarFacturaProveedor: (id: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/factura-proveedor`, { method: 'POST' }),

  devolverSolicitud: (id: string, observacion: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/devolver`, { method: 'POST', body: { observacion } }),

  aprobarSolicitud: (id: string, observacion?: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/aprobar`, { method: 'POST', body: { observacion } }),

  rechazarSolicitud: (id: string, observacion: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/rechazar`, { method: 'POST', body: { observacion } }),

  /**
   * Baja de la compra antes del pago, a pedido de su dueño o de Administración.
   * La app anula la orden de compra en Flexxus como parte de la operación: si el
   * ERP no la deja, esto falla y la compra sigue viva.
   */
  cancelarSolicitud: (id: string, motivo: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/cancelar`, { method: 'POST', body: { motivo } }),

  /**
   * Registra el pago. El contador ya pagó en Flexxus: acá va el N° de pago que le
   * dio el ERP (en `comprobanteNumero`, el campo de siempre). Antes se suben el
   * comprobante de la transferencia (`COMPROBANTE_PAGO`), la orden de pago
   * (`ORDEN_PAGO`) y, si hubo, las retenciones (`RETENCION`).
   *
   * La respuesta dice qué pasó con el respaldo que se le manda al proveedor.
   */
  registrarPago: (
    id: string,
    body: {
      comprobanteNumero: string;
      /** Los correos del proveedor que se tildaron. Vacío = el respaldo va solo a las internas. */
      correosProveedor: string[];
      /** Los que se escribieron a mano, fuera de la ficha. Van en el "Para"; no se guardan. */
      correosAdicionales: string[];
    },
  ) =>
    api<Solicitud & { correoProveedor: ResultadoBackupPago }>(`/api/solicitudes/${id}/pago`, {
      method: 'POST',
      body,
    }),

  /**
   * Para el respaldo del pago: los correos de la ficha del proveedor en Flexxus
   * (`opciones`, para tildar) y las casillas internas que van siempre (`internos`).
   */
  correosDelProveedor: (codigoFlexxus: string) =>
    api<{ opciones: string[]; invalidos: string[]; internos: string[] }>(
      `/api/config/proveedores/${encodeURIComponent(codigoFlexxus)}/correos`,
    ),

  registrarRecepcion: (
    id: string,
    body: {
      renglones: Array<{ id: string; cantidadRecibida: number }>;
      conforme: boolean;
      detalleDiferencia?: string;
    },
  ) => api<Solicitud>(`/api/solicitudes/${id}/recepcion`, { method: 'POST', body }),

  resolverDiferencia: (id: string, detalle: string) =>
    api<Solicitud>(`/api/solicitudes/${id}/resolver-diferencia`, {
      method: 'POST',
      body: { detalle },
    }),

  listarColaboradores: () =>
    api<Array<{ id: string; nombre: string }>>('/api/solicitudes/colaboradores'),

  aprobacionesPendientes: () =>
    api<Solicitud[]>('/api/aprobaciones/pendientes'),
  recepcionPendientes: (q?: string) =>
    api<Solicitud[]>(`/api/recepcion/pendientes${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  listarNotificaciones: () =>
    api<{ items: NotificacionDto[] }>('/api/notificaciones'),
  marcarLeida: (id: string) =>
    api(`/api/notificaciones/${id}/leer`, { method: 'POST' }),
  marcarTodasLeidas: () => api('/api/notificaciones/leer-todas', { method: 'POST' }),

  // Configuración — ADMIN
  listarParametros: () =>
    api<Array<{ clave: string; valor: unknown }>>('/api/config/parametros'),
  actualizarParametro: (clave: string, valor: unknown) =>
    api(`/api/config/parametros/${clave}`, { method: 'PUT', body: { valor } }),

  listarMatriz: () =>
    api<
      Array<{
        id: number;
        evento: string;
        rolDestino: Rol;
        canal: 'EMAIL' | 'APP';
        activo: boolean;
      }>
    >('/api/config/matriz-notificaciones'),
  toggleMatriz: (id: number, activo: boolean) =>
    api(`/api/config/matriz-notificaciones/${id}`, { method: 'PUT', body: { activo } }),

  /**
   * Buzones por rol y, en `RESPALDO_PAGO`, las casillas internas que van en copia
   * del respaldo del pago al proveedor (2026-09-25; antes era una variable de
   * entorno). Todo separado con punto y coma.
   */
  listarBuzonesArea: () => api<Partial<Record<ClaveBuzon, string>>>('/api/config/buzones-area'),
  actualizarBuzonesArea: (body: Partial<Record<ClaveBuzon, string | null>>) =>
    api('/api/config/buzones-area', { method: 'PUT', body }),

  listarUsuarios: () =>
    api<
      Array<{
        id: string;
        email: string;
        nombre: string;
        roles: Rol[];
        /** Roles que resolvió Entra: App Roles y/o grupos de seguridad (no editables). */
        rolesEntra: Rol[];
        /** Roles asignados a mano por un ADMIN. Inertes si `gobernadoPorEntra`. */
        rolesExtra: Rol[];
        /** Entra es la fuente de verdad de esta persona: los roles se tocan en el portal. */
        gobernadoPorEntra: boolean;
        activo: boolean;
        referenteArea: boolean;
        codigoUsuarioFlexxus: string | null;
        ultimoAcceso: string | null;
        /** Cuándo pidió acceso desde /sin-acceso. Null = no hay pedido pendiente. */
        accesoPedidoEn: string | null;
        accesoMotivo: string | null;
        /** Lo que dijo necesitar. Es una sugerencia: el rol lo decide el ADMIN. */
        accesoRolSugerido: Rol | null;
        /** El código del ERP que declara tener. NO está asignado: hay que verificarlo. */
        accesoCodigoFlexxus: string | null;
      }>
    >('/api/config/usuarios'),
  actualizarUsuario: (
    id: string,
    body: Partial<{
      activo: boolean;
      referenteArea: boolean;
      codigoUsuarioFlexxus: string | null;
      rolesExtra: Rol[];
      /** Solo `false`: devuelve a la app el gobierno de los roles (ver ConfigUsuarios). */
      gobernadoPorEntra: boolean;
      /** Da por atendido el pedido de acceso sin asignar ningún rol. */
      descartarPedidoAcceso: true;
    }>,
  ) => api(`/api/config/usuarios/${id}`, { method: 'PUT', body }),

  // ---------- Pedido de acceso (sin sesión: lo usa /sin-acceso) ----------
  // Quien entró con su cuenta y no tiene ningún rol. La identidad sale de la
  // cookie que dejó el login, no de lo que escriba: nadie pide acceso por otro.
  // `silent401: true` porque acá el 401 **es una respuesta**, no una sesión
  // vencida: significa "no venís de un login que rebotó". Sin esto, el cliente
  // lo trataba como cualquier 401 y mandaba a /api/auth/login?next=/sin-acceso,
  // que con una sesión válida vuelve a entrar y saca a la persona del SPA.
  estadoPedidoAcceso: () =>
    api<{
      nombre: string;
      email: string;
      pedidoEn: string | null;
      /** Casilla que recibe el pedido, para poder nombrarla en pantalla. */
      buzon: string | null;
    }>('/api/auth/pedido-acceso', { silent401: true }),
  pedirAcceso: (body: { motivo: string; rolSugerido?: Rol | null; codigoFlexxus?: string | null }) =>
    api<{ ok: true; pedidoEn: string }>('/api/auth/pedido-acceso', { method: 'POST', body }),

  // ---------- Preasignación de accesos por correo (ADMIN) ----------
  // Deja listos los roles y el usuario de Flexxus de alguien ANTES de su primer
  // ingreso: la fila de `usuarios` no existe hasta que entra. `usuario` viene
  // con datos si esa dirección ya usó la app.
  listarPreasignaciones: () =>
    api<
      Array<{
        id: string;
        email: string;
        roles: Rol[];
        codigoUsuarioFlexxus: string | null;
        nota: string | null;
        creadoPor: string | null;
        creadoEn: string;
        usuario: {
          nombre: string;
          email: string;
          activo: boolean;
          ultimoAcceso: string | null;
          codigoUsuarioFlexxus: string | null;
        } | null;
      }>
    >('/api/config/preasignaciones'),

  guardarPreasignacion: (body: {
    email: string;
    roles: Rol[];
    codigoUsuarioFlexxus?: string | null;
    nota?: string | null;
  }) => api('/api/config/preasignaciones', { method: 'PUT', body }),

  borrarPreasignacion: (id: string) =>
    api(`/api/config/preasignaciones/${id}`, { method: 'DELETE' }),

  /**
   * Los usuarios del ERP listos para cargar de una, con el correo propuesto.
   * `emailConfirmado: false` = la dirección es una conjetura armada con el
   * nombre; hay que revisarla antes de guardar.
   */
  preasignacionesSugeridas: () =>
    api<{
      dominio: string | null;
      items: Array<{
        codigo: string;
        nombre: string;
        perfil: string | null;
        emailSugerido: string | null;
        emailConfirmado: boolean;
        yaCargadoCon: string | null;
      }>;
    }>('/api/config/preasignaciones/sugeridas'),

  guardarPreasignacionesMasivo: (
    items: Array<{ email: string; codigoUsuarioFlexxus: string; roles?: Rol[]; nota?: string | null }>,
  ) =>
    api<{ guardadas: number; errores: Array<{ email: string; error: string }> }>(
      '/api/config/preasignaciones/masivo',
      { method: 'POST', body: { items } },
    ),

  /**
   * Sube la planilla de altas (.xlsx o .csv) con el par correo ↔ usuario de
   * Flexxus. Con `dryRun` no escribe nada: devuelve lo que haría, fila por fila,
   * que es lo que la pantalla muestra antes de pedir confirmación.
   */
  importarPlanillaPreasignaciones: async (
    file: File,
    dryRun: boolean,
  ): Promise<ImportacionPlanilla> => {
    const fd = new FormData();
    fd.append('archivo', file);
    const res = await fetch(`/api/config/preasignaciones/importar${dryRun ? '?dryRun=1' : ''}`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error ?? `HTTP ${res.status}`);
    }
    return (await res.json()) as ImportacionPlanilla;
  },

  /** Usuarios activos del ERP + quién tiene tomado cada código (ADMIN). */
  listarUsuariosFlexxus: (refrescar = false) =>
    api<
      Array<{
        codigo: string;
        nombre: string;
        perfil: string | null;
        tomadoPor: { id: string; nombre: string } | null;
      }>
    >(`/api/config/usuarios-flexxus${refrescar ? '?refrescar=1' : ''}`),

  // `misUsuariosFlexxus` y `asignarMiCodigoFlexxus` se eliminaron (2026-08-14):
  // nadie se autoasigna su usuario del ERP. Lo carga un ADMIN por
  // `guardarPreasignacionesMasivo` / `Config → Usuarios`.

  crearProveedor: (body: {
    razonSocial: string;
    cuit: string;
    direccion: string;
    telefono: string;
    condicionIva: string;
    ingresosBrutos: string;
  }) => api<ProveedorCreado>('/api/config/proveedores', { method: 'POST', body }),

  listarTodosProveedores: (q?: string) => {
    const qs = new URLSearchParams({ soloActivos: 'false', take: '200' });
    if (q) qs.set('q', q);
    return api<ResultadoLista<Proveedor>>(`/api/config/proveedores?${qs.toString()}`);
  },
  actualizarProveedor: (
    id: string,
    body: Partial<{ razonSocial: string; cuit: string | null; flexxusId: string | null; activo: boolean }>,
  ) => api<Proveedor>(`/api/config/proveedores/${id}`, { method: 'PUT', body }),

  panelKpis: (desde?: string, hasta?: string) => {
    const qs = new URLSearchParams();
    if (desde) qs.set('desde', desde);
    if (hasta) qs.set('hasta', hasta);
    return api<{
      semaforo: Record<string, { verde: number; amarillo: number; rojo: number }>;
      tiempos: Record<string, { avg: number; median: number; max: number; count: number }>;
      umbrales: Record<string, { verde: number; amarillo: number }>;
      /** Las 5 más atrasadas (pasaron el umbral amarillo), ya ordenadas. */
      demoradas: Array<{
        id: string;
        numero: string;
        ocNumeroFlexxus: string | null;
        estado: EstadoSolicitud;
        proveedor: string | null;
        diasEnEstado: number;
      }>;
    }>(`/api/panel/kpis?${qs.toString()}`);
  },

  pendientesAgenda: () =>
    api<{
      /** `sinFactura`: compras propias frenadas esperando la factura del proveedor. */
      solicitante: { borradores: number; observadas: number; sinFactura: number };
      /** Las dos bandejas del contador: armar la pre compra y registrar el pago. */
      administracion: { porPrePago: number; porPagar: number };
      autorizador: { pendientes: number };
      recepcion: { pendientes: number };
      totalVisibles: number;
    }>('/api/pendientes'),

  actividadReciente: (take = 15) =>
    api<
      Array<{
        id: string;
        tipo: string;
        detalle: Record<string, unknown> | null;
        creadoEn: string;
        solicitud: {
          id: string;
          numero: string;
          proveedor: { razonSocial: string } | null;
        } | null;
        usuario: { nombre: string } | null;
      }>
    >(`/api/pendientes/actividad-reciente?take=${take}`),

  buscarPorNumero: (numero: string) =>
    api<{ id: string; numero: string }>(`/api/solicitudes/por-numero/${encodeURIComponent(numero)}`, {
      silent401: true,
    }).catch(() => null),

  listarAudit: (params: Record<string, string | undefined> = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
    return api<{
      items: Array<{
        id: string;
        tipo: string;
        creadoEn: string;
        detalle: Record<string, unknown> | null;
        solicitud: {
          id: string;
          numero: string;
          ocNumeroFlexxus: string | null;
          estado: string;
        } | null;
        usuario: { id: string; nombre: string; email: string } | null;
      }>;
      nextCursor: string | null;
    }>(`/api/audit/eventos?${qs.toString()}`);
  },

  listarEmailLog: () =>
    api<
      Array<{
        id: string;
        solicitudId: string | null;
        destinatarios: string[];
        asunto: string;
        estado: string;
        error: string | null;
        enviadoEn: string;
      }>
    >('/api/audit/email-log'),

  aprobacionesMias: () =>
    api<
      Array<{
        id: string;
        decision: 'APROBADA' | 'RECHAZADA';
        observacion: string | null;
        decididoEn: string;
        solicitud: {
          id: string;
          numero: string;
          ocNumeroFlexxus: string | null;
          /** La orden de pre pago que se firmó: es lo primero que muestra el historial. */
          prePagoNumeroFlexxus: string | null;
          estado: string;
          /** Desembolso estimado; el modelo no tiene un "total a pagar" propio. */
          totalConIva: number | string;
          totalNeto: number | string;
          /** Para mostrar el importe en la moneda de la compra ("USD 1.200,00"). */
          codigoMonedaFlexxus: string | null;
          cotizacion: number | string | null;
          proveedor: { razonSocial: string } | null;
        };
      }>
    >('/api/aprobaciones/mias'),
};

/** Lo que se edita en Configuración → Buzones: un buzón por rol y el respaldo del pago. */
export type ClaveBuzon = 'AUTORIZADOR' | 'ADMINISTRACION' | 'RECEPCION' | 'RESPALDO_PAGO';

export type NotificacionDto = {
  id: string;
  usuarioId: string;
  solicitudId: string | null;
  tipo: string;
  titulo: string;
  cuerpo: string | null;
  leida: boolean;
  creadaEn: string;
};

export type EventoDto = {
  id: string;
  solicitudId: string | null;
  usuarioId: string | null;
  tipo: string;
  detalle: Record<string, unknown> | null;
  creadoEn: string;
  usuario: { nombre: string; email: string } | null;
};

export type AprobacionDto = {
  id: string;
  solicitudId: string;
  autorizadorId: string;
  decision: 'APROBADA' | 'RECHAZADA';
  observacion: string | null;
  decididoEn: string;
  autorizador: { nombre: string; email: string };
};
